/**
 * UpgradePrompt Component
 *
 * @description プランアップグレード誘導コンポーネント - Issue #286対応
 * @features プラン比較、料金表示、Stripe Checkout統合
 */

import React, { useState } from 'react';
import { PLAN_PRICES, formatPrice } from '@/constants/subscriptionPlans';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import {
  X as CloseIcon,
  CheckCircle as CheckIcon,
  XCircle as CancelIcon,
  TrendingUp as UpgradeIcon,
  Star as StarIcon,
  CreditCard as PaymentIcon,
  Shield as SecurityIcon,
  Loader2,
} from 'lucide-react';
import stripeService from '../../services/stripeService';
import type { PlanInfo } from '../../types/billing';
import { useUpdateSubscription } from '@/hooks/subscription/useUpdateSubscription';

/**
 * プラン順序定義（比較用）
 */
const PLAN_ORDER = {
  free: 0,
  light: 1,
  standard: 2,
  premium: 3,
} as const;

/**
 * プラン変更のアクションタイプを判定
 */
const getActionType = (
  currentPlanId: string,
  targetPlanId: string
): 'upgrade' | 'downgrade' | 'change' => {
  const currentLevel =
    PLAN_ORDER[currentPlanId as keyof typeof PLAN_ORDER] ?? 0;
  const targetLevel = PLAN_ORDER[targetPlanId as keyof typeof PLAN_ORDER] ?? 0;

  if (targetLevel > currentLevel) return 'upgrade';
  if (targetLevel < currentLevel) return 'downgrade';
  return 'change';
};

/**
 * プラン変更のアクションテキストを取得
 */
const getActionText = (
  actionType: 'upgrade' | 'downgrade' | 'change'
): string => {
  switch (actionType) {
    case 'upgrade':
      return 'アップグレード';
    case 'downgrade':
      return 'ダウングレード';
    case 'change':
      return '変更';
    default:
      return '変更';
  }
};

/**
 * プラン定義（3プラン体系）
 */
const PLANS: Record<string, PlanInfo> = {
  light: {
    id: 'light' as const,
    name: 'ライトプラン',
    description: '小規模サロン向けの基本プラン',
    monthlyFee: PLAN_PRICES.light.monthly,
    yearlyFee: PLAN_PRICES.light.annual,
    billingCycle: 'monthly',
    features: [
      {
        id: 'staff_5',
        name: 'スタッフ登録',
        description: '最大5名まで',
        category: 'core',
        included: true,
      },
      {
        id: 'reservation_unlimited',
        name: '月間予約',
        description: '無制限',
        category: 'core',
        included: true,
      },
      {
        id: 'customer_1000',
        name: '顧客管理',
        description: '1,000名まで',
        category: 'core',
        included: true,
      },
      {
        id: 'storage_10',
        name: 'データ容量',
        description: '10GB',
        category: 'core',
        included: true,
      },
      {
        id: 'basic_report',
        name: '基本レポート',
        description: '売上・予約分析',
        category: 'core',
        included: true,
      },
      {
        id: 'email_support',
        name: 'サポート',
        description: 'メールサポート',
        category: 'core',
        included: true,
      },
    ],
    limits: {
      staffCount: 5,
      reservationsPerMonth: 999999,
      customersLimit: 1000,
      storageGB: 10,
      aiAnalysisCount: 100000,
      supportLevel: 'email',
    },
    isActive: true,
  },
  standard: {
    id: 'standard' as const,
    name: 'スタンダードプラン',
    description: '成長中のサロン向け、AI分析機能付き',
    monthlyFee: PLAN_PRICES.standard.monthly,
    yearlyFee: PLAN_PRICES.standard.annual,
    billingCycle: 'monthly',
    features: [
      {
        id: 'staff_20',
        name: 'スタッフ登録',
        description: '最大20名まで',
        category: 'core',
        included: true,
      },
      {
        id: 'reservation_unlimited',
        name: '月間予約',
        description: '無制限',
        category: 'core',
        included: true,
      },
      {
        id: 'customer_5000',
        name: '顧客管理',
        description: '5,000名まで',
        category: 'core',
        included: true,
      },
      {
        id: 'storage_50',
        name: 'データ容量',
        description: '50GB',
        category: 'core',
        included: true,
      },
      {
        id: 'ai_analysis',
        name: 'AI分析',
        description: '月1,000,000 tokens',
        category: 'advanced',
        included: true,
      },
      {
        id: 'external_api',
        name: '外部連携',
        description: 'ホットペッパー等',
        category: 'advanced',
        included: true,
      },
      {
        id: 'advanced_report',
        name: '詳細レポート',
        description: 'カスタムレポート',
        category: 'advanced',
        included: true,
      },
      {
        id: 'chat_support',
        name: 'サポート',
        description: 'チャットサポート',
        category: 'advanced',
        included: true,
      },
    ],
    limits: {
      staffCount: 20,
      reservationsPerMonth: 999999,
      customersLimit: 5000,
      storageGB: 50,
      aiAnalysisCount: 1000000,
      supportLevel: 'chat',
    },
    isActive: true,
  },
  premium: {
    id: 'premium' as const,
    name: 'プレミアムプラン',
    description: '大規模サロン・チェーン向け完全機能',
    monthlyFee: PLAN_PRICES.premium.monthly,
    yearlyFee: PLAN_PRICES.premium.annual,
    billingCycle: 'monthly',
    features: [
      {
        id: 'staff_unlimited',
        name: 'スタッフ登録',
        description: '無制限',
        category: 'premium',
        included: true,
      },
      {
        id: 'reservation_unlimited',
        name: '月間予約',
        description: '無制限',
        category: 'premium',
        included: true,
      },
      {
        id: 'customer_20000',
        name: '顧客管理',
        description: '20,000名まで',
        category: 'premium',
        included: true,
      },
      {
        id: 'storage_100',
        name: 'データ容量',
        description: '100GB',
        category: 'premium',
        included: true,
      },
      {
        id: 'ai_tokens_premium',
        name: 'AI分析',
        description: '月5,000,000 tokens',
        category: 'premium',
        included: true,
      },
      {
        id: 'api_full',
        name: 'API完全アクセス',
        description: 'カスタム統合可能',
        category: 'premium',
        included: true,
      },
      {
        id: 'white_label',
        name: 'ホワイトラベル',
        description: '独自ブランディング',
        category: 'premium',
        included: true,
      },
      {
        id: 'custom_branding',
        name: 'カスタムブランド',
        description: '独自ブランディング',
        category: 'premium',
        included: true,
      },
      {
        id: 'phone_support',
        name: '専任サポート',
        description: '電話・優先対応',
        category: 'premium',
        included: true,
      },
    ],
    limits: {
      staffCount: 999999, // 実質無制限
      reservationsPerMonth: 999999,
      customersLimit: 20000,
      storageGB: 100,
      aiAnalysisCount: 5000000,
      supportLevel: 'phone',
    },
    isActive: true,
  },
};

/**
 * UpgradePrompt Props
 */
interface UpgradePromptProps {
  /** 現在のプラン（完全なPlanInfoまたはidのみ） */
  currentPlan?: PlanInfo | { id: string };
  /** 推奨プラン */
  recommendedPlan?: 'light' | 'standard' | 'premium';
  /** 機能名（特定機能からのアップグレード） */
  feature?: string;
  /** ダイアログを開く */
  open: boolean;
  /** ダイアログを閉じる */
  onClose: () => void;
  /** カスタムタイトル */
  title?: string;
  /** カスタムメッセージ */
  message?: string;
}

/**
 * プラン比較テーブル
 */
const PlanComparisonTable: React.FC<{
  currentPlanId?: string;
  targetPlanId?: string;
  recommendedPlan?: string;
  onSelectPlan: (planId: string) => void;
}> = ({
  currentPlanId = 'light',
  targetPlanId,
  recommendedPlan,
  onSelectPlan,
}) => {
  const features = [
    { key: 'スタッフ登録', light: '5名', standard: '20名', premium: '無制限' },
    { key: '月間予約', light: '無制限', standard: '無制限', premium: '無制限' },
    {
      key: '顧客管理',
      light: '1,000名',
      standard: '5,000名',
      premium: '20,000名',
    },
    { key: 'データ容量', light: '10GB', standard: '50GB', premium: '100GB' },
    {
      key: 'AI分析',
      light: '100,000 tokens/月',
      standard: '1,000,000 tokens/月',
      premium: '5,000,000 tokens/月',
    },
    { key: '外部API連携', light: '×', standard: '◯', premium: '◯' },
    { key: 'ホワイトラベル', light: '×', standard: '×', premium: '◯' },
    { key: 'カスタムブランド', light: '×', standard: '×', premium: '◯' },
    {
      key: 'サポート',
      light: 'メール',
      standard: 'チャット',
      premium: '電話・優先',
    },
  ];

  return (
    <div className="mb-6 rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>機能</TableHead>
            {Object.values(PLANS).map((plan) => (
              <TableHead key={plan.id} className="text-center">
                <div className="space-y-2">
                  <p className="text-base font-bold">{plan.name}</p>
                  <p className="text-lg font-semibold text-primary">
                    {formatPrice(plan.monthlyFee)}/月
                  </p>
                  {plan.id === currentPlanId && (
                    <Badge variant="secondary">現在のプラン</Badge>
                  )}
                  {plan.id === recommendedPlan && <Badge>おすすめ</Badge>}
                  {plan.id !== currentPlanId && (
                    <Button
                      size="sm"
                      variant={plan.id === targetPlanId ? 'default' : 'outline'}
                      onClick={() => onSelectPlan(plan.id)}
                      className="mt-2"
                    >
                      選択
                    </Button>
                  )}
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {features.map((feature) => (
            <TableRow key={feature.key}>
              <TableCell>{feature.key}</TableCell>
              <TableCell className="text-center">
                {feature.light === '◯' ? (
                  <CheckIcon className="inline h-5 w-5 text-green-600" />
                ) : feature.light === '×' ? (
                  <CancelIcon className="inline h-5 w-5 text-muted-foreground" />
                ) : (
                  feature.light
                )}
              </TableCell>
              <TableCell className="text-center">
                {feature.standard === '◯' ? (
                  <CheckIcon className="inline h-5 w-5 text-green-600" />
                ) : feature.standard === '×' ? (
                  <CancelIcon className="inline h-5 w-5 text-muted-foreground" />
                ) : (
                  feature.standard
                )}
              </TableCell>
              <TableCell className="text-center">
                {feature.premium === '◯' ? (
                  <CheckIcon className="inline h-5 w-5 text-green-600" />
                ) : feature.premium === '×' ? (
                  <CancelIcon className="inline h-5 w-5 text-muted-foreground" />
                ) : (
                  feature.premium
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/**
 * UpgradePrompt Component
 */
export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  currentPlan,
  recommendedPlan = 'standard',
  feature,
  open,
  onClose,
  title,
  message,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<string>(recommendedPlan);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  const { mutate: updateSubscription, isPending: isUpdating } =
    useUpdateSubscription();

  const currentPlanId = currentPlan?.id || 'light';
  const targetPlan = PLANS[selectedPlan];

  // アクションタイプとテキストを決定
  const actionType = getActionType(currentPlanId, selectedPlan);
  const actionText = getActionText(actionType);

  // デフォルトタイトルを動的に設定
  const displayTitle = title || `プランを${actionText}`;

  // Stripe Checkoutセッションを作成
  const handleUpgrade = () => {
    setError(null);

    updateSubscription(
      {
        newPlanId: selectedPlan,
      },
      {
        onError: () => {
          setError(`${actionText}処理中にエラーが発生しました。`);
        },
      }
    );
  };

  // Stripeカスタマーポータルへリダイレクト
  const handleManageSubscription = async () => {
    try {
      await stripeService.redirectToCustomerPortal();
    } catch (err) {
      setError('カスタマーポータルへの移動に失敗しました。');
      console.error('Customer portal error:', err);
    }
  };

  const steps = ['プラン選択', '内容確認', '支払い'];

  // カスタムクローズハンドラー（状態をリセット）
  const handleClose = () => {
    setActiveStep(0); // ステップをリセット
    setError(null); // エラーもクリア
    setSelectedPlan(recommendedPlan); // 選択プランもリセット
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl" data-testid="upgrade-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UpgradeIcon className="h-5 w-5 text-primary" />
              <span>{displayTitle}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <CloseIcon className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {message && (
            <Alert>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          {feature && (
            <Alert variant="default" className="border-yellow-200 bg-yellow-50">
              <StarIcon className="h-4 w-4 text-yellow-600" />
              <AlertDescription>
                「{feature}」機能を利用するには、{PLANS[recommendedPlan].name}
                以上への{actionText}が必要です。
              </AlertDescription>
            </Alert>
          )}

          {/* Custom Stepper */}
          <div className="flex items-center justify-between">
            {steps.map((label, index) => (
              <React.Fragment key={label}>
                <div className="flex items-center gap-2">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                      index === activeStep
                        ? 'border-primary bg-primary text-primary-foreground'
                        : index < activeStep
                          ? 'border-green-600 bg-green-600 text-white'
                          : 'border-muted-foreground bg-muted text-muted-foreground'
                    }`}
                  >
                    {index < activeStep ? (
                      <CheckIcon className="h-4 w-4" />
                    ) : (
                      <span className="text-sm">{index + 1}</span>
                    )}
                  </div>
                  <span
                    className={`text-sm ${index === activeStep ? 'font-semibold' : ''}`}
                  >
                    {label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`h-px flex-1 ${index < activeStep ? 'bg-green-600' : 'bg-muted-foreground'}`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {activeStep === 0 && (
            <PlanComparisonTable
              currentPlanId={currentPlanId}
              targetPlanId={selectedPlan}
              recommendedPlan={recommendedPlan}
              onSelectPlan={setSelectedPlan}
            />
          )}

          {activeStep === 1 && (
            <Card className="border-2 border-primary">
              <CardContent className="space-y-4 p-6">
                <h3 className="text-2xl font-semibold">{targetPlan.name}</h3>
                <p className="text-muted-foreground">
                  {targetPlan.description}
                </p>

                <Separator />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      月額料金
                    </p>
                    <p className="text-3xl font-bold text-primary">
                      {formatPrice(targetPlan.monthlyFee)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      年額料金（10%割引）
                    </p>
                    <p className="text-3xl font-bold text-secondary">
                      {targetPlan.yearlyFee
                        ? formatPrice(targetPlan.yearlyFee)
                        : '-'}
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <p className="mb-3 text-sm font-medium">含まれる機能</p>
                  <div className="space-y-2">
                    {targetPlan.features.slice(0, 5).map((f) => (
                      <div key={f.id} className="flex items-start gap-3">
                        <CheckIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                        <div>
                          <p className="font-medium">{f.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {f.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeStep === 2 && (
            <div className="space-y-4">
              <Card className="text-center">
                <CardContent className="space-y-4 p-6">
                  <PaymentIcon className="mx-auto h-12 w-12 text-primary" />
                  <h3 className="text-lg font-semibold">安全な決済処理</h3>
                  <p className="text-sm text-muted-foreground">
                    決済処理はStripeを通じて安全に行われます。
                    カード情報は暗号化され、安全に保護されます。
                  </p>
                  <div className="flex justify-center gap-2">
                    <Badge variant="outline" className="gap-1">
                      <SecurityIcon className="h-3 w-3" />
                      SSL暗号化
                    </Badge>
                    <Badge variant="outline" className="gap-1">
                      <CheckIcon className="h-3 w-3" />
                      PCI準拠
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isUpdating}>
            キャンセル
          </Button>

          {activeStep > 0 && (
            <Button
              variant="outline"
              onClick={() => setActiveStep(activeStep - 1)}
              disabled={isUpdating}
            >
              戻る
            </Button>
          )}

          {activeStep < steps.length - 1 && (
            <Button
              onClick={() => setActiveStep(activeStep + 1)}
              disabled={isUpdating}
            >
              次へ
            </Button>
          )}

          {activeStep === steps.length - 1 && (
            <>
              <Button
                variant="outline"
                onClick={handleManageSubscription}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <PaymentIcon className="mr-2 h-4 w-4" />
                )}
                既存プランを管理
              </Button>
              <Button onClick={handleUpgrade} disabled={isUpdating}>
                {isUpdating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <UpgradeIcon className="mr-2 h-4 w-4" />
                )}
                {isUpdating ? '処理中...' : `${targetPlan.name}に${actionText}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradePrompt;
